// eslint-disable-next-line @octopusdeploy/custom-portal-rules/no-restricted-imports
import { css } from "@emotion/css";
import { text, themeTokens } from "@octopusdeploy/design-system-tokens";
import classnames from "classnames";
import type { PropsWithChildren } from "react";
import * as React from "react";
import type { ExpandableContainerProps } from "~/components/Expandable/index";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import CardTitle from "~/components/form/Sections/CardTitle";
import type { CardFill } from "~/components/form/Sections/ExpandableFormSection";
import type { FormSectionBaseProps } from "~/components/form/Sections/FormSectionBase";
import { FormSectionBase } from "~/components/form/Sections/FormSectionBase";
import { isSummaryNode } from "~/components/form/Sections/Summary";
import styles from "~/components/form/Sections/style.module.less";
import type { SummaryNode } from "~/components/form/index";
// Mostly a copy of ExpandableFormSectionProps
interface ProducerSuppliedFormSectionProps extends FormSectionBaseProps {
    summary: SummaryNode | React.ReactNode;
    title: string;
    chip?: React.ReactElement;
    help?: React.ReactNode; //todo: mattr: make mandatory
    expandable?: boolean; //todo: this prop will move to the new FormSection component
    focusOnExpandAll?: boolean;
    fillCardWidth?: CardFill;
    overflowMenuItems?: Array<MenuItem | MenuItem[]>;
    useCardTitleContainerStyleForSummary?: boolean;
    hasInitialFocus?: boolean;
    contextualHelp?: React.ReactNode;
}
type Props = ProducerSuppliedFormSectionProps & ExpandableContainerProps;
// Do not use this component outside of process templates.
// This component exists as an easy way of changing an ExpandableFormSection to a readonly FormSection. It is not meant to be a permanent solution.
export default function ProducerSuppliedFormSection(props: PropsWithChildren<Props>) {
    return <ReadonlyFormSection title={props.title} help={isSummaryNode(props.summary) ? props.summary.node : props.summary} includeBorder={true}/>;
}
interface FormSectionProps extends FormSectionBaseProps {
    title?: React.ReactNode;
    help?: React.ReactNode;
    includeBorder?: boolean;
}
// Mostly a copy of FormSection
class ReadonlyFormSection extends FormSectionBase<FormSectionProps> {
    render() {
        return (<div className={classnames(newStyles.readonly, this.props.includeBorder ? styles.formBorder : null)}>
                {this.props.title && (<CardTitle title={<div className={newStyles.cardTitleContainer}>
                                <span className={newStyles.cardTitle}>{this.props.title}</span>
                                <span className={classnames(newStyles.cardSubTitle, this.props.forceMobileBehaviour && styles.cardSubTitleForceMobile)}>{this.renderChild(this.props.help || <div />)}</span>
                            </div>}/>)}
                {this.props.children && <div className={newStyles.cardMedia}>{this.renderChildren(this.props.children)}</div>}
            </div>);
    }
    static displayName = "ReadonlyFormSection";
}
// Styles needed to be copied as all the colours were being forced with !important so it was impossible to override them with a separate class.
const newStyles = {
    readonly: css({
        backgroundColor: themeTokens.color.background.secondary.transparent,
        width: "100%",
        boxShadow: "none",
        color: themeTokens.color.text.tertiary,
    }),
    cardTitleContainer: css({
        display: "flex",
        alignItems: "top",
        wordWrap: "break-word",
        padding: "0.75rem 1rem",
        color: "",
        position: "relative",
        flexDirection: "row",
        flexGrow: "1",
        "@media (max-width: @screen-md)": {
            flexDirection: "column",
        },
    }),
    cardTitle: css({
        width: "15rem !important",
        minWidth: "15rem !important",
        minHeight: "1.5rem !important",
        font: text.body.bold.large,
        textAlign: "left",
        "@media (max-width: @screen-md)": {
            textAlign: "left",
            position: "static",
        },
        "&& > div": {
            marginLeft: "4px",
            verticalAlign: "middle",
            display: "inline-block",
            bottom: "1px",
        },
    }),
    cardSubTitle: css({
        textAlign: "left",
        marginLeft: "1.6rem !important",
        marginRight: "2rem !important",
        display: "block !important",
        flexGrow: "1",
        wordBreak: "break-word",
        minWidth: "50%",
        "@media (max-width: @screen-md)": {
            marginLeft: "0rem !important",
        },
        "> div > p": {
            margin: 0,
        },
    }),
    cardMedia: css({
        marginLeft: "17.6rem",
        marginRight: "4rem",
        padding: "0.25rem 0 1rem 0 !important", // Overriding MUI padding.
        maxWidth: "37.5rem",
        "@media (max-width: @screen-md)": {
            marginLeft: "1.1rem !important",
            marginRight: "1.1rem",
        },
    }),
};
